<template>
    <div class="row">
        <div class="small-12 columns text-align-center">
            <h4>Bezifferung des Schadens</h4>
            <p v-if="hasNegativOrEqualMinValue">
                Der mögliche Schadensersatz beträgt bis zu {{ possibleCompensationMaxString }} €.
            </p>
            <p v-else>
                Der mögliche Schadensersatz bewegt sich zwischen {{ possibleCompensationMinString }} €
                und {{ possibleCompensationMaxString }} €.
            </p>
            <p>
                Innerhalb dieses Rahmens müssen Sie bestimmen, welchen Betrag Sie gegenüber dem Hersteller einfordern möchten.
                Hierbei sollten Sie sich von der Erwägung leiten lassen, dass die Geltendmachung eines geringeren Schadens
                eine größere Wahrscheinlichkeit in sich trägt, dass der Hersteller den Schadensersatz außergerichtlich leistet,
                und umgekehrt die Geltendmachung eines höheren Schadens die Wahrscheinlichkeit für eine weitere gerichtliche
                Auseinandersetzung erhöht. Leistet der Hersteller außergerichtlich keinen Schadensersatz, kann die
                Schadensersatzforderung in einer späteren gerichtlichen Auseinandersetzung aber noch erhöht werden.
            </p>
        </div>
        <div class="small-12 columns" :class="{ error: showCompensationErrorText }">
            <label class="bold-text">
                Bitte wählen Sie deshalb hier jetzt innerhalb des bestehenden Rahmens (siehe oben) den Schadensersatzbetrag ein,
                den Sie gegenüber dem Hersteller geltend machen wollen:
                <vue3-slider
                    v-model="formData.selected_compensation"
                    :color="viewData.sliderOptions.color"
                    :track-color="viewData.sliderOptions.trackColor"
                    :min="sliderMinValue"
                    :max="sliderMaxValue"
                    alwaysShowHandle
                    :height="viewData.sliderOptions.height"
                    :step="viewData.sliderOptions.step"
                    @click.stop.prevent
                />
                <div class="row input-row">
                    <div class="small-4 columns">
                        <input class="compensation-input" v-model="formData.selected_compensation" type="number" @click.stop.prevent/>
                    </div>
                </div>

                <small class="error no-capitalize" v-if="showCompensationErrorText">
                    {{ compensationErrorText }}
                </small>
            </label>
        </div>
        <template v-if="isFormValid && isLocalFormValid">
            <div class="small-12 columns text-align-center">
                Vielen Dank für Ihre Angaben. Bitte klicken Sie auf Abschicken um Ihr Schreiben an den Hersteller zu schicken.
            </div>
            <div class="small-12 columns hint">
                <div class="panel radius">
                    <h4 class="text-align-center">Wichtiger Hinweis</h4>
                    <p>
                        Der Geltendmachung von Schadensersatzansprüchen gegenüber dem Hersteller liegt die Annahme
                        zugrunde, dass Sie, wenn Sie von der Abgasmanipulation an ihrem Fahrzeug gewusst hätten, sich
                        für den Kauf eines anderen gleichwertigen Fahrzeugs entschieden und dabei die gleiche Form der
                        Finanzierung gewählt hätten. Auch liegt die Annahme zugrunde, dass Sie keine weiteren Ausstattungen
                        für das Fahrzeug erworben haben, die sie keiner anderen Verwendung mehr zuführen können.
                        Treffen diese Annahmen nicht zu, stehen Ihnen ggf. noch höhere Schadensersatzansprüche gegenüber
                        dem Hersteller zu. Diese können bis zum Ablauf der Verjährung auch noch gesondert gegenüber
                        dem Hersteller eingefordert werden. Das Forderungsschreiben selbst bewirkt – so oder so − keine
                        Hemmung der Verjährung, sondern setzt die Gegenseite nur in Verzug.
                    </p>
                </div>
            </div>
        </template>
        <div v-else class="small-12 columns not-valid-text">
            Bitte füllen Sie das Formular vollständig aus.
        </div>
    </div>
</template>

<script>

import TypeHelper from '../../helper/TypeHelper';
import Validation from '../../helper/Validation';
import Calculation from '../../helper/Calculation';
import slider from 'vue3-slider';

export default {
    name: 'FinalStep',
    components: {'vue3-slider': slider},
    props: {
        isFormValid: Boolean,
        caseData: {
            type: Object,
            default: function()
            {
                return {
                    mandator_lawform:                       null,
                    mandator_prename:                       null,
                    mandator_surname:                       null,
                    mandator_mail:                          null,
                    mandator_iban:                          null,
                    second_mandator_lawform:                null,
                    second_mandator_prename:                null,
                    second_mandator_surname:                null,
                    manufacturer_id:                        null,
                    purchase_date:                          null,
                    emissions_type:                         null,
                    mileage_purchase:                       null,
                    mileage:                                null,
                    price:                                  null,
                    is_vehicle_sold:                        null,
                    was_new:                                null,
                    selected_compensation:                  null
                }
            }
        },
    },
    data: () => ({
        formData: {
            selected_compensation: 0
        },
        validation: {
            isFormDirty: false,
            isCompensationDirty: false
        },
        defaultSelectValues: [
            {
                value: true,
                title: 'Ja'
            },
            {
                value: false,
                title: 'Nein'
            }
        ],
        viewData: {
            sliderOptions: {
                color: '#e10065',
                trackColor: 'rgba(255, 0, 101, 0.5)',
                height: 20,
                step: 1
            },
            currencyInputOptions: {
                "currency": "EUR",
                "currencyDisplay": "symbol",
                "valueRange": {
                    "min": 0
                },
                "hideCurrencySymbolOnFocus": true,
                "hideGroupingSeparatorOnFocus": true,
                "hideNegligibleDecimalDigitsOnFocus": true,
                "autoDecimalDigits": false,
                "autoSign": true,
                "useGrouping": true,
                "accountingSign": false
            },
        }
    }),
    computed: {
        showCompensationErrorText()
        {
            return (this.validation.isFormDirty || this.validation.isCompensationDirty)
                && TypeHelper.isNotNullOrUndefined(this.compensationErrorText);
        },
        compensationErrorText()
        {
            let validators = [
                Validation.isNotNull
            ];

            let validationResult = Validation.runValidation(this.formData.selected_compensation, validators);

            if (TypeHelper.isNullOrUndefined(validationResult))
            {
                if (this.formData.selected_compensation < this.calculatedAvgCompensationMinWithUsingAdvantage
                    || this.formData.selected_compensation > this.calculatedAvgCompensationMaxWithUsingAdvantage)
                    validationResult = 'Bitte wählen Sie einen Wert der innerhalb des angegebenen Bereichs liegt';
            }

            return validationResult;
        },
        isLocalFormValid()
        {
            let isValid = true;
            isValid = isValid && (this.compensationErrorText === null);

            return isValid;
        },
        hasNegativOrEqualMinValue()
        {
            let minValue = Calculation.calculateAvgCompensationMinWithUsingAdvantage(this.caseData);
            let maxValue = Calculation.calculateAvgCompensationMaxWithUsingAdvantage(this.caseData)
            return minValue <= 0 || minValue === maxValue;
        },
        calculatedAvgCompensationMinWithUsingAdvantage()
        {
            if(this.isCalculationPossible)
            {
                let compensation = Math.round(Calculation.calculateAvgCompensationMinWithUsingAdvantage(this.caseData));

                if (compensation <= 0 || compensation === this.calculatedAvgCompensationMaxWithUsingAdvantage)
                    return 1;

                return compensation;
            }

            return 0;
        },
        possibleCompensationMinString()
        {
            return parseFloat(this.calculatedAvgCompensationMinWithUsingAdvantage.toFixed(2)).toLocaleString('de-DE');
        },
        calculatedAvgCompensationMaxWithUsingAdvantage()
        {
            if(this.isCalculationPossible)
            {
                let compensation = Math.round(Calculation.calculateAvgCompensationMaxWithUsingAdvantage(this.caseData));

                if (compensation <= 0)
                    return 1;

                return compensation;
            }

            return 0;
        },
        possibleCompensationMaxString()
        {
            return parseFloat(this.calculatedAvgCompensationMaxWithUsingAdvantage.toFixed(2)).toLocaleString('de-DE');
        },
        isCalculationPossible()
        {
            return Calculation.isCalculationPossible(this.caseData);
        },
        sliderMinValue()
        {
            if (this.calculatedAvgCompensationMaxWithUsingAdvantage <= this.calculatedAvgCompensationMinWithUsingAdvantage )
                return this.calculatedAvgCompensationMaxWithUsingAdvantage - 1;

            return this.calculatedAvgCompensationMinWithUsingAdvantage;
        },
        sliderMaxValue()
        {
            return this.calculatedAvgCompensationMaxWithUsingAdvantage;
        }
    },
    methods: {
        validate()
        {
            this.validation.isFormDirty = true;

            this.emitValidity();
        },
        emitValidity()
        {
            this.$emit('updateValidity', this.isLocalFormValid);
        },
        emitChangedData()
        {
            this.$emit('changeCaseData', this.formData);
        },
        convertCurrencyValueToString(currencyValue)
        {
            return parseFloat(currencyValue).toLocaleString('de-DE');
        }
    },
    watch: {
        calculatedAvgCompensationMaxWithUsingAdvantage(newValue)
        {
            let diff = Math.round((newValue - this.calculatedAvgCompensationMinWithUsingAdvantage) / 2);
            this.formData.selected_compensation = newValue - diff;
        },
        'formData.selected_compensation': function()
        {
            this.validation.isCompensationDirty = true;
        },
        formData: {
            handler: function()
            {
                this.emitChangedData();
                this.emitValidity();
            },
            deep: true
        },
    }
};
</script>

<style scoped>
    .not-valid-text {
        margin-top: 20px;
        margin-bottom: 30px;
        text-align: center;
    }

    .text-align-center {
        text-align: center;
    }

    .hint {
        margin-top: 20px;
    }

    .error label {
        color: #4d4d4d;
    }

    select {
        margin: 0 0 1rem;
    }

    .error select {
        margin-bottom: 0;
    }

    .bold-text {
        font-weight: bold;
    }

    .no-capitalize {
        text-transform: none!important;
    }

    .input-row {
        margin-top: 10px;
        display: flex;
        justify-content: center;
    }

    .compensation-input {
        text-align: center;
    }
</style>
